import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import PaperStepBody from '@linc-technologies/linced-common/components/paper-step-body';
import PaperStepActions from '@linc-technologies/linced-common/components/paper-step-actions';

export default class PaperStep extends Component {
  bodyComponent = PaperStepBody;
  actionsComponent = PaperStepActions;

  // orderedIndex is assigned by the parent stepper (via `updateLabels`) once the
  // step has registered. `_autoStepNumber` holds the parent-assigned fallback
  // used when the consumer did not pass an explicit `@stepNumber`.
  @tracked orderedIndex;
  @tracked _autoStepNumber;

  // Pass-through getters expose the step's args as instance properties so the
  // parent stepper template can read `step.label`, `step.error`, etc. off each
  // registered child instance (as it did with the classic component).
  get label() {
    return this.args.label;
  }

  get summary() {
    return this.args.summary;
  }

  get error() {
    return this.args.error;
  }

  get optional() {
    return this.args.optional;
  }

  get optionalLabel() {
    return this.args.optionalLabel ?? 'Optional';
  }

  // stepNumber prefers an explicit `@stepNumber`, falling back to the value the
  // parent assigns on registration.
  get stepNumber() {
    return this.args.stepNumber ?? this._autoStepNumber;
  }

  get isActive() {
    return this.orderedIndex === this.args.currentStep;
  }

  get isActiveAndHorizontal() {
    return this.isActive && !this.args.vertical;
  }

  get isCompleted() {
    return (
      Boolean(this.args.linear) && this.orderedIndex < this.args.currentStep
    );
  }

  get hasError() {
    return Boolean(this.args.error);
  }

  get stepNumberLabel() {
    return this.orderedIndex + 1;
  }

  get isButtonDisabled() {
    return Boolean(this.args.linear) || this.isActive;
  }

  // Register with the parent stepper (passed as `@parentComponent`) after the
  // element is inserted. This replaces the ember-composability-tools ChildMixin,
  // removed as a dependency in ember-paper 1.0.0. Registration must run *after*
  // render (via {{did-insert}}, not a reactive modifier) because it mutates the
  // parent's tracked `childComponents`, which the parent template reads while
  // rendering — doing so mid-render triggers a backtracking assertion.
  @action
  registerWithParent() {
    this.args.parentComponent?.registerChild(this);
  }

  // Unregister on teardown via the Glimmer lifecycle hook (no destroy modifier
  // needed). Guarded so we don't reindex a parent that is itself tearing down.
  willDestroy() {
    super.willDestroy(...arguments);
    let parent = this.args.parentComponent;
    if (parent && !parent.isDestroying && !parent.isDestroyed) {
      parent.unregisterChild(this);
    }
  }
}
